import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Press, haptic } from '@dorixona/ui'
import { BackButton } from '../../ui/BackButton'
import { Sheet } from '../../ui/Sheet'
import { Icon } from '../../ui/Icon'
import { LoginCta } from '../../ui/LoginCta'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useSession } from '../../app/session'
import { AddCardSheet } from '../checkout/AddCardSheet'
import { PayIcon } from '../checkout/PayIcon'
import { useCards, useDeleteCard, type SavedCard } from '../checkout/cards'
import { cardTail } from '../checkout/model'
import { cardName } from './cardName'

/**
 * «Мои карты» (R-100…R-102): сохранённые карты, привязка новой — номер и
 * срок, затем код из СМС банка — и удаление. Выключены карты у магазина —
 * привязка ответит `upstream_failed`, и шторка так и скажет.
 * Стили — `.saved-cards` (`.acc-cards` занят старой сеткой карт из прототипа).
 */
export function CardsScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation(['account', 'checkout', 'common'])
  const { isAuthenticated } = useSession()
  const cards = useCards(isAuthenticated)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<SavedCard | null>(null)
  const list = cards.data ?? []
  const first = list[0]

  return (
    <section className="screen cart-wrap">
      <div className="mhead">
        <BackButton to="/profile" />
        <h1>{t('account:cards.title')}</h1>
      </div>

      {!isAuthenticated ? (
        <LoginCta
          onSignIn={() => {
            void navigate('/login')
          }}
        />
      ) : cards.isPending ? (
        <Loading what={t('common:state.loading')} count={2} />
      ) : cards.isError ? (
        <Failed
          error={cards.error}
          onRetry={() => {
            void cards.refetch()
          }}
        />
      ) : (
        <>
          {first ? (
            <ul className="acc-card saved-cards">
              {list.map((card) => (
                <li key={card.id}>
                  <PayIcon code="saved_card" />
                  <span className="t1 num">{cardName(card)}</span>
                  <Press
                    squish="row"
                    className="saved-cards__del"
                    aria-label={t('account:cards.removeOne', { card: cardTail(card.number) })}
                    onPress={() => {
                      setRemoving(card)
                    }}
                  >
                    {t('account:cards.remove')}
                  </Press>
                </li>
              ))}
            </ul>
          ) : (
            <div className="card empty">
              <Icon name="i-card" size={54} />
              <div className="t">{t('account:cards.emptyTitle')}</div>
              {t('account:cards.empty')}
            </div>
          )}
          <Button
            className="acc-add"
            aria-haspopup="dialog"
            onPress={() => {
              setAdding(true)
            }}
          >
            {t('checkout:cards.add')}
          </Button>
          {/* Закрытая шторка держит любую карту из списка: так ей не нужен «пустой» случай. */}
          {first ? (
            <RemoveCardSheet
              card={removing ?? first}
              opened={removing !== null}
              onClose={() => {
                setRemoving(null)
              }}
            />
          ) : null}
          <AddCardSheet
            opened={adding}
            onClose={() => {
              setAdding(false)
            }}
          />
        </>
      )}
    </section>
  )
}

interface RemoveCardSheetProps {
  card: SavedCard
  opened: boolean
  onClose: () => void
}

/** Подтверждение: карту удалят и у нас, и у платёжного шлюза. */
function RemoveCardSheet({ card, opened, onClose }: RemoveCardSheetProps) {
  const { t } = useTranslation(['account', 'common'])
  const remove = useDeleteCard()

  const close = () => {
    remove.reset()
    onClose()
  }

  return (
    <Sheet opened={opened} label={t('account:cards.confirmTitle')} onClose={close}>
      <div className="fsheet">
        <div className="fhead">
          <h2>{t('account:cards.confirmTitle')}</h2>
          <Press
            squish="icon"
            className="fclose"
            aria-label={t('common:actions.close')}
            onPress={close}
          >
            <Icon name="i-x" size={18} />
          </Press>
        </div>
        <p className="cardform-lead">{t('account:cards.confirmLead', { card: cardName(card) })}</p>
        {remove.isError ? (
          <p className="acc-note acc-note-err" role="alert">
            {failureText(remove.error, t)}
          </p>
        ) : null}
        <div className="ffoot">
          <Button variant="ghost" onPress={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            className="btn-danger"
            disabled={remove.isPending}
            onPress={() => {
              remove.mutate(card.id, {
                onSuccess: () => {
                  haptic('success')
                  close()
                },
                onError: () => {
                  haptic('error')
                },
              })
            }}
          >
            {remove.isPending ? t('account:cards.removing') : t('account:cards.remove')}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
